import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import type { MarketForecast, MarketPriceRow } from '../../hooks/useMarketAnalysis';
import { formatPrice } from './MarketSummaryCards';

/**
 * 価格チャート。終値 + 移動平均 (SMA25/75) + ボリンジャーバンド (±2σ) に、
 * ARIMA の予測 (破線) と信頼区間 (帯) を同じ時間軸の右側へ続けて描く。
 * 出来高・RSI は別チャート (二軸は使わない)。
 */

// 系列色 (dataviz 検証済み。終値 / SMA25 / SMA75 / 予測)
export const SERIES_COLORS = {
  close: '#1d4ed8',
  sma25: '#d97706',
  sma75: '#7c3aed',
  forecast: '#0d9488',
  band: 'rgba(29, 78, 216, 0.08)',
  forecastBand: 'rgba(13, 148, 136, 0.15)',
};

type Point = {
  trade_date: string;
  close?: number | null;
  sma_25?: number | null;
  sma_75?: number | null;
  bb_range?: [number, number] | null;
  forecast?: number | null;
  forecast_range?: [number, number] | null;
  isForecast: boolean;
};

type Props = {
  rows: MarketPriceRow[];
  forecast: MarketForecast | null;
  currency: string;
  displayName: string;
};

const fmtDate = (d: string) => (d ? `${d.slice(2, 4)}/${d.slice(5, 7)}/${d.slice(8, 10)}` : '');

export default function MarketPriceChart({ rows, forecast, currency, displayName }: Props) {
  const data = useMemo<Point[]>(() => {
    const pts: Point[] = rows.map((r) => ({
      trade_date: r.trade_date,
      close: r.close,
      sma_25: r.sma_25,
      sma_75: r.sma_75,
      bb_range: r.bb_lower != null && r.bb_upper != null ? [r.bb_lower, r.bb_upper] : null,
      isForecast: false,
    }));
    if (forecast && forecast.rows.length > 0 && pts.length > 0) {
      // 予測線を最終実績点から連続させる
      const last = pts[pts.length - 1];
      if (last.close != null) {
        last.forecast = last.close;
        last.forecast_range = [last.close, last.close];
      }
      for (const f of forecast.rows) {
        pts.push({
          trade_date: f.trade_date,
          forecast: f.forecast,
          forecast_range: f.lower != null && f.upper != null ? [f.lower, f.upper] : null,
          isForecast: true,
        });
      }
    }
    return pts;
  }, [rows, forecast]);

  const lastActualDate = rows.length > 0 ? rows[rows.length - 1].trade_date : null;
  const hasForecast = !!forecast && forecast.rows.length > 0;

  // Y 軸は表示範囲に合わせて余白 3% を取る (0 起点にしない。価格系列は変動幅が主役)
  const [yMin, yMax] = useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const p of data) {
      for (const v of [p.close, p.sma_25, p.sma_75, p.forecast, p.bb_range?.[0], p.bb_range?.[1], p.forecast_range?.[0], p.forecast_range?.[1]]) {
        if (v != null && Number.isFinite(v)) { lo = Math.min(lo, v); hi = Math.max(hi, v); }
      }
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [0, 1];
    const pad = (hi - lo) * 0.03 || hi * 0.01;
    return [lo - pad, hi + pad];
  }, [data]);

  const tickInterval = Math.max(0, Math.floor(data.length / 8) - 1);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    const p: Point | undefined = payload[0]?.payload;
    if (!p) return null;
    const line = (color: string, name: string, value: number | null | undefined, dashed = false) => (
      value == null ? null : (
        <Box key={name} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 0, borderTop: `2px ${dashed ? 'dashed' : 'solid'} ${color}` }} />
            <Typography sx={{ fontSize: '11px', fontWeight: 600 }}>{name}</Typography>
          </Box>
          <Typography sx={{ fontSize: '11px', fontVariantNumeric: 'tabular-nums' }}>{formatPrice(value, currency)}</Typography>
        </Box>
      )
    );
    return (
      <Box sx={{ backgroundColor: '#334155', color: '#fff', borderRadius: '8px', px: 1.5, py: 1, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.2)', minWidth: '160px' }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 700, mb: 0.5 }}>
          {label}{p.isForecast ? ' (予測)' : ''}
        </Typography>
        {line(SERIES_COLORS.close, '終値', p.close)}
        {line(SERIES_COLORS.sma25, 'SMA25', p.sma_25)}
        {line(SERIES_COLORS.sma75, 'SMA75', p.sma_75)}
        {p.bb_range && (
          <Typography sx={{ fontSize: '10px', color: '#cbd5e1', mt: 0.25 }}>
            BB ±2σ {formatPrice(p.bb_range[0], currency)} 〜 {formatPrice(p.bb_range[1], currency)}
          </Typography>
        )}
        {p.isForecast && line(SERIES_COLORS.forecast, '予測', p.forecast, true)}
        {p.isForecast && p.forecast_range && (
          <Typography sx={{ fontSize: '10px', color: '#cbd5e1', mt: 0.25 }}>
            80% 区間 {formatPrice(p.forecast_range[0], currency)} 〜 {formatPrice(p.forecast_range[1], currency)}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2, backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#475569', mb: 0.5 }}>
        {displayName} 終値と移動平均・ボリンジャーバンド{hasForecast ? ` + ${forecast!.method} 予測 ${forecast!.rows.length} 営業日` : ''}
      </Typography>
      <ResponsiveContainer width="100%" height={360}>
        <ComposedChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis dataKey="trade_date" tickFormatter={fmtDate} interval={tickInterval} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
          <YAxis domain={[yMin, yMax]} tickFormatter={(v: number) => formatPrice(v, currency)} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={72} />
          <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#94a3b8', strokeDasharray: '3 3' }} />
          <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '4px' }} iconType="plainline" verticalAlign="top" align="right" />
          <Area type="monotone" dataKey="bb_range" name="ボリンジャー ±2σ" stroke="none" fill={SERIES_COLORS.band} isAnimationActive={false} legendType="rect" connectNulls={false} />
          <Area type="monotone" dataKey="forecast_range" name="予測 80% 区間" stroke="none" fill={SERIES_COLORS.forecastBand} isAnimationActive={false} legendType="rect" connectNulls={false} />
          <Line type="monotone" dataKey="sma_25" name="SMA25" stroke={SERIES_COLORS.sma25} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls={false} />
          <Line type="monotone" dataKey="sma_75" name="SMA75" stroke={SERIES_COLORS.sma75} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls={false} />
          <Line type="monotone" dataKey="close" name="終値" stroke={SERIES_COLORS.close} strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} connectNulls={false} />
          <Line type="monotone" dataKey="forecast" name="予測 (ARIMA)" stroke={SERIES_COLORS.forecast} strokeWidth={2} strokeDasharray="5 5" dot={false} activeDot={{ r: 4 }} isAnimationActive={false} connectNulls={false} />
          {hasForecast && lastActualDate && (
            <ReferenceLine x={lastActualDate} stroke="#94a3b8" strokeDasharray="4 2" label={{ value: '実績 | 予測', position: 'insideTopRight', fontSize: 10, fill: '#64748b' }} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
